import React, { useEffect, useState } from 'react';
import { Box, Paper, Typography, Select, MenuItem, Button } from '@mui/material';
import StatsEleves from './StatsEleves';
import StatsControls from './StatsControls';
import api from '../../api';
import { useNavigate, useLocation } from 'react-router-dom';

/**
 * StatsElevesPage
 * - Wrapper for StatsEleves with class selector and admin controls (shown to profs)
 * - If classeId present in query params, preselect it.
 * - If query param from=match, hide the selector and the admin controls (read-only podium view).
 */
export default function StatsElevesPage() {
  const [classes, setClasses] = useState([]);
  const [classeId, setClasseId] = useState('');
  const [isProf, setIsProf] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  // check query params
  const q = new URLSearchParams(location.search);
  const preClass = q.get('classeId') || q.get('classe') || '';
  const fromParam = (q.get('from') || '').toLowerCase();
  const fromMatch = fromParam === 'match';

  useEffect(() => {
    if (preClass) setClasseId(preClass);
  }, [preClass]);

  useEffect(() => {
    let mounted = true;
    async function load() {
      try {
        const res = await api.get('/classes');
        if (!mounted) return;
        setClasses(Array.isArray(res.data) ? res.data : []);
        if (!classeId && !preClass && Array.isArray(res.data) && res.data.length > 0) {
          setClasseId(res.data[0]._id || res.data[0].id);
        }
      } catch (err) {
        console.error('Erreur fetch classes', err);
        setClasses([]);
      }
    }
    load();

    async function detectProf() {
      try {
        const sessionFlag = typeof sessionStorage !== 'undefined' ? sessionStorage.getItem('jugeboxe_auth') : null;
        if (sessionFlag === '1') { if (mounted) setIsProf(true); return; }

        const sessionToken = typeof sessionStorage !== 'undefined' ? sessionStorage.getItem('profAuthToken') : null;
        if (sessionToken) {
          try {
            const me = await api.get('/users/me', { headers: { Authorization: `Bearer ${sessionToken}` } });
            const role = me?.data?.role || me?.data?.user?.role;
            if (role === 'prof' || role === 'teacher' || role === 'enseignant') { if (mounted) setIsProf(true); return; }
          } catch (e) { /* ignore */ }
        }

        try {
          const me2 = await api.get('/users/me');
          const role = me2?.data?.role || me2?.data?.user?.role;
          if (role === 'prof' || role === 'teacher' || role === 'enseignant') { if (mounted) setIsProf(true); return; }
        } catch (e) { /* ignore */ }

        if (mounted) setIsProf(false);
      } catch (e) {
        if (mounted) setIsProf(false);
      }
    }
    detectProf();

    return () => { mounted = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // If we're coming from a match and there's no classeId, show a help message
  const renderFromMatchMessage = () => {
    if (!fromMatch) return null;
    if (!classeId) {
      return <Typography sx={{ color: 'text.secondary' }}>Aucune classe précisée pour ce match.</Typography>;
    }
    return null;
  };

  return (
    <Box sx={{ p: 2, maxWidth: 1000, mx: 'auto' }}>
      <Paper sx={{ p: 2 }}>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 2 }}>
          <Typography variant="h6">Podium / Classement {fromMatch ? '' : '— Sélectionnez la classe'}</Typography>
          <Box sx={{ flex: 1 }} />
          <Button variant="outlined" onClick={() => navigate(-1)}>Retour</Button>
        </Box>

        {/* If not from match, show selector + admin controls. If from match, hide them and show read-only podium for classeId */}
        {!fromMatch ? (
          <>
            <Box sx={{ mb: 2, display: 'flex', gap: 2, alignItems: 'center' }}>
              <Select
                value={classeId}
                onChange={e => setClasseId(e.target.value)}
                sx={{ minWidth: 260 }}
                displayEmpty
              >
                <MenuItem value="">Sélectionner une classe...</MenuItem>
                {classes.map(c => (
                  <MenuItem key={c._id || c.id} value={c._id || c.id}>
                    {c.nom || c.name || c.label || c.title || (c._id || c.id)}
                  </MenuItem>
                ))}
              </Select>
            </Box>

            {/* Admin controls (reset/recompute) shown immediately under the class selector */}
            {isProf ? <StatsControls onDone={() => window.location.reload()} /> : null}

            {!classeId ? (
              <Typography sx={{ color: 'text.secondary' }}>Choisissez une classe pour afficher le podium.</Typography>
            ) : (
              <StatsEleves classeId={classeId} />
            )}
          </>
        ) : (
          // fromMatch === true: read-only view, no selector, no controls
          <>
            {renderFromMatchMessage()}
            {classeId ? <StatsEleves classeId={classeId} /> : null}
          </>
        )}
      </Paper>
    </Box>
  );
}